import React, { useCallback } from 'react';
import { noop } from 'lodash';

import FormError from 'components/form/FormError.component';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import asObject from 'utils/objects/asObject.util';
import isMissing from 'utils/predicates/any/isMissing.util';
import reactChildrenLike from 'utils/react/childrenLike.util';
import reactChildrenUnlike from 'utils/react/childrenUnlike.util';
import hasReactChildren from 'utils/react/hasReactChildren.util';
import { capitalize } from 'utils/strings/manipulate.util';

import CN from './FormField.module.scss';

const CTAG = 'FormField';

const After = ({ children }) => <>{children}</>;
const Before = ({ children }) => <>{children}</>;

const KNOWN_COMPONENTS = Object.freeze([Before, After]);

const fromStringToLabel = (text) => capitalize(fromAnyToTrimmed(text)) + S.colon;

const determineBeforeInput = ({ children, label, name, tag }) => {
  const beforeInput = reactChildrenLike(Before, children);

  // if there is something before the input or after the input, don't bother making up a label contents
  if (beforeInput.length) return beforeInput;

  const afterInput = reactChildrenLike(After, children);
  if (afterInput.length) return beforeInput;

  // if an explicit label is set, use it
  const trimmed = fromAnyToTrimmed(label);
  if (trimmed) return fromStringToLabel(trimmed);

  // no explicit label, but there are other children,
  // this may be a checkbox which doesn't need a label, do nothing
  const otherChildren = reactChildrenUnlike(KNOWN_COMPONENTS, children);
  if (otherChildren.length) return S.empty;

  // try to use some other text from the props, the name or the tag, whichever exists
  const text = trimmed || fromAnyToTrimmed(name) || fromAnyToTrimmed(tag);
  return text ? fromStringToLabel(text) : S.empty;
};

const FormField = ({ input: Input, onChange = noop, onBlur = noop, onValidate = noop, renderError, ...props }) => {
  const tag = fromAnyToTrimmed(props.tag);
  const name = fromAnyToTrimmed(props.name);
  const FTAG = `${CTAG}[${name},${tag}]`;

  const [error, setError] = useStateWhileMounted();

  const handleChange = useCallback(
    (ev) => {
      const changeResult = asObject(call(`${FTAG}.onChange`, onChange, ev));
      const validateResult = asObject(call(`${FTAG}.onValidate`, onValidate, ev));

      const result = { ...changeResult, ...validateResult };
      setError(isMissing(result.error) ? error : result.error);

      return result;
    },
    [FTAG, error, onChange, onValidate, setError],
  );

  const handleBlur = useCallback(
    (ev) => {
      const blurResult = asObject(call(`${FTAG}.onBlur`, onBlur, ev));
      const validateResult = asObject(call(`${FTAG}.onValidate`, onValidate, ev));

      const result = { ...blurResult, ...validateResult };
      setError(isMissing(result.error) ? error : result.error);

      return result;
    },
    [FTAG, error, onBlur, onValidate, setError],
  );

  const afterInput = reactChildrenLike(After, props.children);
  const beforeInput = determineBeforeInput(props);

  return (
    <div data-bem="FormField" data-tag={tag} data-field-name={name} className={CN.component}>
      {/* NOTE: the label has input inside it, it doesn't need htmlFor */}
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      <label data-bem="FormField__label" data-tag={tag} data-field-name={name} className={CN.label}>
        {fromAnyToBool(beforeInput) && (
          <div data-bem="FormField__before" data-tag={tag} data-field-name={name} className={CN.before}>
            {beforeInput}
          </div>
        )}

        <Input {...props} onChange={handleChange} onBlur={handleBlur} />

        {hasReactChildren(afterInput) && (
          <div data-bem="FormField__after" data-tag={tag} data-field-name={name} className={CN.after}>
            {afterInput}
          </div>
        )}
      </label>
      <FormError tag={tag} name={name} value={error} render={renderError} />
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default Object.assign(FormField, { After, Before });
